import type {
  EnumeratedStringVFrame,
  SimpleVFrameBase,
} from '@festina/life-general-types/life'
import type { ExcludeStringLiteral } from '@festina-life/core'
import type { ComponentProps, FC } from 'react'

// oxlint-disable no-restricted-imports
import { LifeDatePicker } from './lifeInput/LifeDatePicker'
import { LifeDoubleInput } from './lifeInput/LifeDoubleInput'
import { LifeEnumeratedInput } from './lifeInput/LifeEnumeratedInput'
import { LifeMultiSelect } from './lifeInput/LifeMultiSelect'
import { LifeNumberInput } from './lifeInput/LifeNumberInput'
import { LifePercentageInput } from './lifeInput/LifePercentageInput'
import { LifeSelect } from './lifeInput/LifeSelect'
import { LifeTextInput } from './lifeInput/LifeTextInput'
// oxlint-enable no-restricted-imports

const inputMap = {
  boolean: LifeSelect,
  date: LifeDatePicker,
  double: LifeDoubleInput,
  enumerated: LifeEnumeratedInput,
  integer: LifeNumberInput,
  string: LifeTextInput,
  percentage: LifePercentageInput,
  array: LifeMultiSelect,
}

export type InputType = keyof typeof inputMap
export type FallbackInputType = ExcludeStringLiteral<InputType, 'enumerated'>

export type LifeInputWrapperType<
  C extends FC<P>,
  T extends ExcludeStringLiteral<InputType, 'enumerated'>,
  V,
  P = ComponentProps<C>,
> = ComponentProps<C> &
  ComponentProps<typeof LifeEnumeratedInput<V>> & {
    fallbackInputType: T
  }

export const fallbackInputTypeToVFrame: Record<
  FallbackInputType,
  { _type: SimpleVFrameBase['_type'] } & Pick<
    EnumeratedStringVFrame,
    'isFramingListValue'
  >
> = {
  boolean: { _type: 'FreeBooleanVFrame' },
  date: { _type: 'DateVFrame' },
  double: { _type: 'RangeDecimalVFrame' },
  percentage: { _type: 'RangeFractionVFrame' },
  string: { _type: 'FreeStringVFrame' },
  integer: {
    _type: 'RangeIntegerVFrame',
  },
  array: { _type: 'EnumeratedStringVFrame', isFramingListValue: true },
}

export const simpleVFrameToInputType: Record<
  SimpleVFrameBase['_type'],
  { inputType: ExcludeStringLiteral<InputType, 'array'> | null }
> = {
  DontUseBooleanVFrame: { inputType: 'boolean' },
  EnumeratedBooleanVFrame: { inputType: 'boolean' },
  FixedBooleanVFrame: { inputType: 'boolean' },
  FreeBooleanVFrame: { inputType: 'boolean' },
  DateVFrame: { inputType: 'date' },
  DontUseDateVFrame: { inputType: null },
  EnumeratedDateVFrame: { inputType: 'enumerated' },
  FixedDateVFrame: { inputType: 'date' },
  RangeDateVFrame: { inputType: 'date' },
  DecimalVFrame: { inputType: 'double' },
  DontUseDecimalVFrame: { inputType: null },
  EnumeratedDecimalVFrame: { inputType: 'enumerated' },
  FixedDecimalVFrame: { inputType: 'double' },
  RangeDecimalVFrame: { inputType: 'double' },
  DontUseEnumVFrame: { inputType: null },
  EnumeratedEnumVFrame: { inputType: 'enumerated' },
  FixedEnumVFrame: { inputType: 'enumerated' },
  DontUseFractionVFrame: { inputType: null },
  EnumeratedFractionVFrame: { inputType: 'enumerated' },
  FixedFractionVFrame: { inputType: 'percentage' },
  RangeFractionVFrame: { inputType: 'percentage' },
  DontUseIntegerVFrame: { inputType: null },
  EnumeratedIntegerVFrame: { inputType: 'enumerated' },
  FixedIntegerVFrame: { inputType: 'integer' },
  RangeIntegerVFrame: { inputType: 'integer' },
  DontUseMoneyDecimalVFrame: { inputType: null },
  EnumeratedMoneyDecimalVFrame: { inputType: 'enumerated' },
  FixedMoneyDecimalVFrame: { inputType: 'double' },
  RangeMoneyDecimalVFrame: { inputType: 'double' },
  DontUseMoneyVFrame: { inputType: null },
  EnumeratedMoneyVFrame: { inputType: 'enumerated' },
  FixedMoneyVFrame: { inputType: 'double' },
  RangeMoneyVFrame: { inputType: 'double' },
  DontUseStringVFrame: { inputType: null },
  EnumeratedStringVFrame: { inputType: 'enumerated' },
  FixedStringVFrame: { inputType: 'string' },
  FreeStringVFrame: { inputType: 'string' },
} as const
